import { useCallback, useEffect, useMemo, useState } from 'react'

import { useFocusNodeById, useSetFocus } from '@please/lrud'
import { useLocation } from 'react-router-dom'
import { scroller } from 'react-scroll'

import { useAuth } from '@sportworld/js-sdk/src/context/authContext'

import { getCataloguePages } from '@/actions'
import { Direction } from '@/constants/focus'
import {
  mainMenuHomePageItems,
  mainMenuDefaultPageItems,
  mainMenuProfilePageItems,
  MainMenuNames,
  offersMenuItem,
} from '@/constants/menu'
import { paths } from '@/constants/paths'
import { scrollOptions } from '@/constants/scrollers'
import useStore from '@/store/useStore'
import { MenuItemType } from '@/types/menuTypes'
import { isDisabledMenuItem } from '@/utils/utils'

export const useMainMenuFocus = (
  menuItems: MenuItemType[],
  focusId: string,
  defaultFocusChildRewrite?: number,
) => {
  const { pathname } = useLocation()
  const [defaultFocusChild, setDefaultFocusChild] = useState(1)
  const focusNode = useFocusNodeById(focusId)
  const setFocus = useSetFocus()
  const [isMounted, setIsMounted] = useState<boolean>(false)

  useEffect(() => {
    if (!focusNode || !isMounted) {
      return
    }
    const focusedChildIndex = focusNode.focusedChildIndex

    if (focusedChildIndex !== null && focusedChildIndex !== defaultFocusChild) {
      setDefaultFocusChild(focusedChildIndex)
    }
  }, [isMounted, focusNode?.focusedChildIndex])

  const onMove = (e: MoveEvent) => {
    const focusedChildIndex =
      e.direction === Direction.Forward
        ? Number(e.prevChildIndex) + 1
        : Number(e.prevChildIndex) - 1
    setDefaultFocusChild(focusedChildIndex)
  }
  useEffect(() => {
    const children = focusNode?.children
    if (!children || !children.length) {
      return
    }

    const defaultFocusChild = menuItems.findIndex(
      ({ path }) =>
        pathname === path ||
        (pathname?.includes(paths.PAGE) && path === paths.SPORTS) ||
        (pathname?.includes(paths.FAVORITES) && path === paths.FAVORITES),
    )

    const correctedDefaultFocusChild =
      defaultFocusChild >= 0 ? defaultFocusChild : 1

    const childToFocus =
      children[defaultFocusChildRewrite ?? correctedDefaultFocusChild]
    setDefaultFocusChild(defaultFocusChildRewrite ?? correctedDefaultFocusChild)
    focusNode.isFocused && setFocus(childToFocus)
    setIsMounted(true)
  }, [pathname, !!focusNode?.children?.length, defaultFocusChildRewrite])

  return [defaultFocusChild, onMove] as const
}

export const useSubMenuFocus = (
  menuItems: MenuItemType[] | undefined,
  isOpen: boolean,
  isSidebarFocused: boolean,
  focusId: string,
  idPrefix: string,
  disableSubMenuAutoOpen?: boolean,
) => {
  const { pathname } = useLocation()
  const setFocus = useSetFocus()
  const [defaultFocusChild, setDefaultFocusChild] = useState(0)

  const idFromPathname = pathname?.split('/')[2]
  const focusNode = useFocusNodeById(focusId)

  useEffect(() => {
    if (
      focusNode &&
      focusNode.focusedChildIndex !== null &&
      focusNode?.focusedChildIndex !== defaultFocusChild
    ) {
      setDefaultFocusChild(focusNode.focusedChildIndex)
    }
  }, [focusNode?.focusedChildIndex])

  useEffect(() => {
    if (menuItems?.length) {
      let subFocusIndex = menuItems.findIndex(
        (item) => item.id === idFromPathname,
      )
      subFocusIndex = subFocusIndex < 0 ? 0 : subFocusIndex
      setDefaultFocusChild(subFocusIndex)
      if (isOpen) {
        scroller.scrollTo(idPrefix + idFromPathname, {
          ...scrollOptions.MenuScroll,
          containerId: idPrefix + scrollOptions.MenuScroll.containerId,
        })
      }
    }
  }, [menuItems, isOpen, idPrefix])

  useEffect(() => {
    if (isOpen && isSidebarFocused && menuItems && !disableSubMenuAutoOpen) {
      const subFocusIndex = menuItems.findIndex(
        (item) => item.id === idFromPathname,
      )
      if (subFocusIndex === defaultFocusChild) {
        setFocus(idFromPathname)
      }
    }
  }, [isOpen, isSidebarFocused, disableSubMenuAutoOpen])

  const onMove = (e: MoveEvent) => {
    const currentChildIndex =
      e.direction === Direction.Forward
        ? Number(e.prevChildIndex) + 1
        : Number(e.prevChildIndex) - 1
    setDefaultFocusChild(currentChildIndex)
    scroller.scrollTo(e.nextChildNode.focusId, {
      ...scrollOptions.MenuScroll,
      containerId: idPrefix + scrollOptions.MenuScroll.containerId,
    })
  }

  return [defaultFocusChild, onMove] as const
}

export const useMainMenuItems = () => {
  const appConfig = useStore((state) => state.common.appConfig)
  const cataloguePages = useStore((state) => state.common.cataloguePages)
  const favoritePages = useStore((state) => state.favoritePage.favoritePages)

  const { isRegisteredUser } = useAuth()

  useEffect(() => {
    getCataloguePages(appConfig.cataloguePagesLimit)
  }, [appConfig])

  const filterMenuItems = useCallback(
    ({ hidden, name }: MenuItemType) => {
      const isSpecialHomePage = name === MainMenuNames.SPECIAL_HOME
      const skipSpecialHomePage =
        isSpecialHomePage &&
        !appConfig.specialHome &&
        !appConfig.specialHomePageId

      const isSportsPage = name === MainMenuNames.SPORTS
      const skipSportsPage =
        isSportsPage && !appConfig.sportsPage && !appConfig.sportsPageId

      const isCatalogue = name === MainMenuNames.CATALOGUE
      const skipCatalogue =
        isCatalogue && !appConfig.catalogue && !appConfig.cataloguePageIds

      const isHomeTabPage = name === MainMenuNames.HOME
      const skipHomeTabPage = isHomeTabPage && !appConfig.home?.homeTab

      const isClassicHome = name === MainMenuNames.CLASSIC_HOME
      const skipClassicHome = isClassicHome && !appConfig.home?.classicHome

      const isFocusPage = name === MainMenuNames.FOCUS_PAGE
      const skipFocusPage =
        isFocusPage && !appConfig.focusPage && !appConfig.focusPageId

      if (
        skipSpecialHomePage ||
        skipSportsPage ||
        skipCatalogue ||
        skipHomeTabPage ||
        skipClassicHome ||
        skipFocusPage
      ) {
        return false
      }

      return !hidden && !isDisabledMenuItem(appConfig, name)
    },
    [appConfig, isRegisteredUser],
  )

  const mainMenuProfileItems = useMemo(
    () => mainMenuProfilePageItems.filter(filterMenuItems),
    [filterMenuItems],
  )

  const mainMenuHomeItems = useMemo(
    () => mainMenuHomePageItems.filter(filterMenuItems),
    [filterMenuItems],
  )

  const mainMenuDefaultItems = useMemo(
    () => mainMenuDefaultPageItems.filter(filterMenuItems),
    [filterMenuItems],
  )

  const mainMenuSettingsPageItems = [offersMenuItem]

  const mainMenuSettingsItems = useMemo(
    () => mainMenuSettingsPageItems.filter(filterMenuItems),
    [filterMenuItems],
  )

  const sportsMenuItems = useMemo(() => {
    if (!favoritePages) {
      return cataloguePages?.items
    }

    return cataloguePages?.items?.map<MenuItemType>((page) =>
      favoritePages[page.id] ? { ...page, isFavorite: true } : page,
    )
  }, [cataloguePages, favoritePages])

  return {
    mainMenuProfileItems,
    mainMenuHomeItems,
    mainMenuDefaultItems,
    mainMenuSettingsItems,
    sportsMenuItems,
  }
}
